import {
  ScheduledBankPaymentGroupStateEnum,
  TScheduledBankPaymentGroupState
} from '@/features/payments/types/BatchPayment';
import { EnumValues } from '@/services/types/EnumValues';

/** Which set of action controls the scheduled payment modal renders. */
export const PaymentActionVariantEnum = {
  NONE: 'none',
  DELETE: 'delete',
  APPROVAL_REQUIRED: 'approval_required',
  AWAITING_ADDITIONAL_APPROVAL: 'awaiting_additional_approval',
  // The current user isn't a signatory on the multi-sign action at all - unlike
  // AWAITING_ADDITIONAL_APPROVAL (they acted, others are pending), they have no Approve/Reject to
  // do here, ever, so no action controls render for this variant.
  NO_APPROVAL_AUTHORITY: 'no_approval_authority',
  // Only ever reached via ScheduledPaymentModal (a batch group never renders there), since a
  // FAILED batch group is handled entirely by BatchOverviewModal's own Mark as complete/Retry
  // pair instead of this variant.
  RETRY_AND_DELETE: 'retry_and_delete'
} as const;
export type TPaymentActionVariant = EnumValues<typeof PaymentActionVariantEnum>;

/**
 * The single source of truth for "what can you do to a payment in this state".
 *
 * Typed as a full `Record` on purpose: when the backend adds a new group state, this map
 * fails to compile until the new state declares its action, instead of silently falling
 * through to no buttons.
 */
export const ACTION_BY_STATE: Record<TScheduledBankPaymentGroupState, TPaymentActionVariant> = {
  [ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL]: PaymentActionVariantEnum.APPROVAL_REQUIRED,
  [ScheduledBankPaymentGroupStateEnum.APPROVED]: PaymentActionVariantEnum.DELETE,
  // Already processing or finished - nothing the user can action from the modal (yet).
  [ScheduledBankPaymentGroupStateEnum.IN_PROGRESS]: PaymentActionVariantEnum.NONE,
  [ScheduledBankPaymentGroupStateEnum.COMPLETED]: PaymentActionVariantEnum.NONE,
  [ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES]: PaymentActionVariantEnum.NONE,
  // Reusing FAILED for individual scheduled payments too (see services/scheduledPayment.ts) - a
  // failed one-off payment can be retried or deleted, with no delete-block-window check since it
  // has already run (see openDeleteDialog in useScheduledPaymentAction, which only checks the
  // window for the DELETE variant, i.e. an upcoming APPROVED payment).
  [ScheduledBankPaymentGroupStateEnum.FAILED]: PaymentActionVariantEnum.RETRY_AND_DELETE,
  [ScheduledBankPaymentGroupStateEnum.REJECTED]: PaymentActionVariantEnum.NONE,
  [ScheduledBankPaymentGroupStateEnum.EXPIRED]: PaymentActionVariantEnum.NONE
};
